<template>
  <Dialog
    :title="mode === 'nested' ? 'Download Nested Files' : 'Download All Production Files'"
    :show-close-button="phase !== 'running'"
    :show-footer="true"
    :custom-footer="true"
    md
    @closeEvent="close"
  >
    <template #modal-content>
      <!-- SETUP: panels to include (both modes); sizes + label content (nested) -->
      <div v-if="phase === 'setup'" class="flex flex-col gap-4">
        <p v-if="mode === 'nested'" class="text-sm text-gray-600">
          Nesting <span class="font-semibold">{{ productLabel }}</span> onto
          {{ nestSettings?.paper_width }}×{{ nestSettings?.paper_length }} in paper. Pick what to
          include, generate, then review the packing before downloading.
        </p>
        <p v-else class="text-sm text-gray-600">
          Production files for <span class="font-semibold">{{ productLabel }}</span> —
          {{ allRows.length }} roster row(s), one composite per row plus the overview sheet. Pick
          the panels to include, then generate.
        </p>

        <fieldset class="flex flex-col gap-1 text-sm">
          <span class="font-medium">Panels to include</span>
          <p class="text-xs text-gray-500">
            Every panel is included by default. Untick the ones sourced elsewhere or cut from dyed
            fabric — they are left out of every file in this download.
          </p>
          <div v-if="panelsLoading" class="flex items-center gap-2 text-xs text-gray-500 py-1">
            <span
              class="inline-block size-3 rounded-full border-2 border-primary border-r-transparent animate-spin"
            ></span>
            Detecting panels…
          </div>
          <p v-else-if="!panelOptions.length" class="text-xs text-amber-600">
            No panels could be detected — all artwork will be included.
          </p>
          <template v-else>
            <label v-for="opt in panelOptions" :key="opt.name" class="flex items-center gap-2">
              <input v-model="selectedPanels" type="checkbox" :value="opt.name" />
              <span>
                {{ opt.label }}
                <span class="text-xs text-gray-500">({{ opt.name }})</span>
              </span>
            </label>
            <div class="flex gap-3 text-xs mt-1">
              <button type="button" class="text-primary underline" @click="selectAllPanels">
                Select all
              </button>
              <button type="button" class="text-primary underline" @click="selectedPanels = []">
                None
              </button>
            </div>
          </template>
        </fieldset>

        <fieldset v-if="mode === 'nested'" class="flex flex-col gap-1 text-sm">
          <span class="font-medium">Sizes to nest</span>
          <label v-for="opt in sizeOptions" :key="opt.size" class="flex items-center gap-2">
            <input v-model="selectedSizes" type="checkbox" :value="opt.size" />
            <span>
              {{ opt.size || '(no size)' }}
              <span class="text-xs text-gray-500">
                — {{ opt.rows }} row(s), {{ opt.quantity }} garment(s)
              </span>
            </span>
          </label>
        </fieldset>

        <fieldset v-if="mode === 'nested'" class="flex flex-col gap-1 text-sm">
          <span class="font-medium">Sheets</span>
          <label class="flex items-start gap-2">
            <input v-model="sheetGrouping" type="radio" value="order" class="mt-1" />
            <span>
              Whole order together
              <span class="block text-xs text-gray-500">
                Pieces of every roster row share sheets — fewest sheets, rows mixed.
              </span>
            </span>
          </label>
          <label class="flex items-start gap-2">
            <input v-model="sheetGrouping" type="radio" value="row" class="mt-1" />
            <span>
              One sheet set per roster row
              <span class="block text-xs text-gray-500">
                As many of a row's pieces as fit on one sheet; what does not fit continues on the
                next sheet of the same row. Rows are never mixed.
              </span>
            </span>
          </label>
        </fieldset>

        <fieldset v-if="mode === 'nested'" class="flex flex-col gap-1 text-sm">
          <span class="font-medium">Piece labels (besides the size)</span>
          <label class="flex items-center gap-2">
            <input v-model="labelOrderNo" type="checkbox" />
            <span>Order number</span>
          </label>
          <label class="flex items-center gap-2">
            <input v-model="labelPlayer" type="checkbox" />
            <span>Player name / number</span>
          </label>
          <label class="flex items-center gap-2">
            <input v-model="labelPanel" type="checkbox" />
            <span>Panel name</span>
          </label>
        </fieldset>
      </div>

      <!-- RUNNING: per-row progress -->
      <div v-else-if="phase === 'running' || phase === 'done'" class="flex flex-col gap-3">
        <p class="text-sm text-gray-600">
          <template v-if="mode === 'nested'">
            Composing the selected roster rows of
            <span class="font-semibold">{{ productLabel }}</span> and splitting the cut panels.
          </template>
          <template v-else>
            Generating one production SVG per roster row for
            <span class="font-semibold">{{ productLabel }}</span
            >. Files are zipped when every row has finished.
          </template>
        </p>

        <div class="w-full bg-gray-200 rounded-full h-2.5">
          <div
            class="bg-primary h-2.5 rounded-full transition-all"
            :style="{ width: `${progressPercent}%` }"
          ></div>
        </div>
        <p class="text-sm text-gray-700">{{ progressLabel }}</p>

        <ul class="max-h-64 overflow-y-auto divide-y divide-gray-100 text-sm">
          <li v-for="item in items" :key="item.rowIndex" class="flex items-center gap-2 py-1.5">
            <span
              class="inline-block w-2.5 h-2.5 rounded-full shrink-0"
              :class="statusDotClass(item.status)"
            ></span>
            <span class="truncate">{{ item.label }}</span>
            <span v-if="item.message" class="ml-auto text-xs text-gray-500 truncate">
              {{ item.message }}
            </span>
          </li>
        </ul>

        <p v-if="doneWithWarnings" class="text-sm text-amber-600">
          Finished with warnings — details are in validation.txt inside the zip.
        </p>
      </div>

      <!-- PREVIEW (nested): pack summary + adjustable settings -->
      <div v-else-if="phase === 'preview'" class="flex flex-col gap-4">
        <div v-if="packResult" class="text-sm">
          <p class="font-medium">
            <template v-if="packing">Packing…</template>
            <template v-else
              >{{ packedPieceCount }} piece(s) on {{ packResult.sheets.length }} sheet(s)</template
            >
            <span v-if="packResult.oversized.length" class="text-red-600">
              — {{ packResult.oversized.length }} oversized piece(s) left out
            </span>
          </p>
          <ul class="mt-1 text-gray-600">
            <li v-for="(sheet, i) in packResult.sheets" :key="i">
              {{ sheetFileStem(i, sheet) }}
              <template v-if="sheet.group"> — {{ sheet.group }}</template>
              <template v-if="sheet.garments?.length">
                — garments {{ garmentRange(sheet.garments) }}</template
              >
              — {{ sheet.placements.length }} pieces — {{ (sheet.utilization * 100).toFixed(1) }}%
              utilization
            </li>
          </ul>
          <p v-if="rowsWithIssues" class="mt-1 text-amber-600">
            {{ rowsWithIssues }} row(s) had warnings — see validation.txt in the zip.
          </p>
        </div>

        <fieldset class="grid grid-cols-2 gap-3 text-sm">
          <label class="flex flex-col gap-1">
            <span class="font-medium">Piece padding (in)</span>
            <input
              v-model.number="paddingIn"
              type="number"
              min="0"
              max="8"
              step="0.0625"
              class="border rounded-md px-2 py-1.5"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="font-medium">Cut outline width (mm)</span>
            <input
              v-model.number="cutWidthMm"
              type="number"
              min="0.05"
              max="10"
              step="0.05"
              class="border rounded-md px-2 py-1.5"
            />
          </label>
          <div class="flex flex-col gap-1">
            <span class="font-medium">Rotation</span>
            <label class="flex items-center gap-2">
              <input v-model="rotate90" type="checkbox" /> <span>90°</span>
            </label>
            <label class="flex items-center gap-2">
              <input v-model="rotate180" type="checkbox" /> <span>180° (directional fabric)</span>
            </label>
          </div>
          <div class="flex flex-col gap-1">
            <label class="flex items-center gap-2 font-medium">
              <input v-model="searchOutline" type="checkbox" /> <span>Searching outline</span>
            </label>
            <div v-if="searchOutline" class="grid grid-cols-2 gap-2">
              <label class="flex flex-col gap-0.5 text-xs">
                <span>Offset (mm)</span>
                <input
                  v-model.number="searchOffsetMm"
                  type="number"
                  min="1"
                  max="100"
                  step="0.5"
                  class="border rounded-md px-2 py-1"
                />
              </label>
              <label class="flex flex-col gap-0.5 text-xs">
                <span>Thickness (mm)</span>
                <input
                  v-model.number="searchWidthMm"
                  type="number"
                  min="0.1"
                  max="50"
                  step="0.5"
                  class="border rounded-md px-2 py-1"
                />
              </label>
            </div>
          </div>
        </fieldset>
        <p class="text-xs text-gray-500">
          Adjustments re-pack instantly — nothing is recomposed. The zip contains every sheet in one
          file (all_sheets.svg) with its cut-lines-only twin, one DXF cut file per sheet,
          nesting_summary.txt and validation.txt.
        </p>
      </div>
    </template>

    <template #footer>
      <template v-if="phase === 'setup'">
        <button class="bg-[#9197b3] rounded-md text-[#3a3a3a] px-3 py-2" @click="close">
          Cancel
        </button>
        <button
          class="bg-primary rounded-md text-white px-3 py-2"
          :disabled="!canGenerate"
          :class="!canGenerate ? 'opacity-50 cursor-not-allowed' : ''"
          :title="
            panelOptions.length && !selectedPanels.length ? 'Select at least one panel' : undefined
          "
          @click="startGenerate"
        >
          Generate
        </button>
      </template>
      <template v-else-if="phase === 'running'">
        <button
          class="bg-[#9197b3] rounded-md text-[#3a3a3a] px-3 py-2"
          @click="cancelRequested = true"
        >
          {{ cancelRequested ? 'Cancelling…' : 'Cancel' }}
        </button>
      </template>
      <template v-else-if="phase === 'preview'">
        <button class="bg-[#9197b3] rounded-md text-[#3a3a3a] px-3 py-2" @click="close">
          Close
        </button>
        <button
          class="bg-[#9197b3] rounded-md text-[#3a3a3a] px-3 py-2 disabled:opacity-50"
          :disabled="packing"
          @click="repack"
        >
          {{ packing ? 'Packing…' : 'Re-pack' }}
        </button>
        <button
          class="bg-primary rounded-md text-white px-3 py-2"
          :disabled="zipping || !packResult?.sheets.length"
          :class="zipping || !packResult?.sheets.length ? 'opacity-50 cursor-not-allowed' : ''"
          @click="downloadNestedZip"
        >
          {{ zipping ? 'Preparing…' : 'Download ZIP' }}
        </button>
      </template>
      <template v-else>
        <button class="bg-primary rounded-md text-white px-3 py-2" @click="close">Close</button>
      </template>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
/**
 * Batch production-file generation over a product's roster rows, strictly one
 * compose at a time (each run creates and disposes its own fabric canvas;
 * assets are memoized across rows).
 *
 * mode 'all'    → one production SVG per roster row + validation.txt, zipped.
 * mode 'nested' → setup (pick sizes + label content) → compose the selected
 *                 rows → PREVIEW the packing (sheets/utilization, padding,
 *                 rotation and outline settings adjustable with instant
 *                 re-pack — nothing recomposes) → zip: every sheet together
 *                 in one file (all_sheets.svg + all_sheets_cutlines.svg), one
 *                 DXF cut file per sheet, nesting_summary.txt and
 *                 validation.txt. Only runs on demand. (Per-sheet SVGs were
 *                 dropped at the factory's request — the combined file is
 *                 what they print from; the DXFs stay until their cutter is
 *                 confirmed to read the combined cut-lines SVG.)
 */
import { computed, onMounted, ref } from 'vue';
import JSZip from 'jszip';
import Dialog from '@/components/Shared/Dialog.vue';
import { showToast } from '@/composables/swal';
import {
  createProductionAssetCache,
  useProductionFileEngine,
} from '@/composables/useProductionFileEngine';
import type { FactoryProduct, OrderDetail } from '@/models';
import type { ProductRosterDetail } from '@/models/ProductRosterDetail';
import type { FactoryNestingSettings } from '@/models/FactoryNestingSettings';
import {
  convertBasicShapesToPaths,
  flattenSourceClipPaths,
} from '@/utils/productionFile/clipFlatten';
import { buildComposeInputForRow } from '@/utils/productionFile/composeInput';
import { buildProductionFileName, sanitizeFileSegment } from '@/utils/productionFile/naming';
import { buildVectorizeLogoUrl, productionStorageUrl } from '@/utils/productionFile/endpoints';
import { inlineCustomLogoVectors } from '@/utils/productionFile/logoVectorize';
import { buildProductionLogoResolver } from '@/composables/useLogoSources';
import { buildOverviewSheet, type OverviewEntry } from '@/utils/productionFile/overviewSheet';
import { finalizeCompositeForCorel } from '@/utils/productionFile/compositeFinish';
import { loadLabelFont } from '@/utils/productionFile/labelGlyphs';
import { nestPieces } from '@/utils/productionFile/shapeNest';
import { splitPieceIslands } from '@/utils/productionFile/pieceIslands';
import {
  buildCombinedSheetsSvg,
  type CombinedSheetEntry,
} from '@/utils/productionFile/combinedSheets';
import {
  addHeaderLabelToComposite,
  computePanelLabelPlacements,
  extractCompositeContent,
  windowSizePt,
  type PanelLabelPlacement,
} from '@/utils/productionFile/panelSplitter';
import { namespaceSvgIds } from '@/utils/productionFile/logoVectorize';
import {
  buildCutlinesDxf,
  buildCutlinesSvg,
  buildNestingSummary,
  buildSheetSvg,
  computeEdgeLabels,
  computePieceAreas,
  computeSearchOutlines,
  computeSheetClipPaths,
  garmentRange,
  packPieces,
  sheetFileStem,
  resolveOutlineSpec,
  DEFAULT_PIECE_PADDING_IN,
  SIZE_LABEL_FONT_PT,
  SIZE_LABEL_MARGIN_PT,
  SIZE_LABEL_OUTLINE_PT,
  type NestPieceInput,
  type NestResult,
} from '@/utils/productionFile/nesting';
import { buildValidationReport } from '@/utils/productionFile/validation';
import { prettyPanelName } from '@/utils/productionFile/panelNames';

type RowStatus = 'pending' | 'generating' | 'done' | 'warning' | 'error' | 'skipped';

interface BatchItem {
  rowIndex: number;
  row: ProductRosterDetail;
  label: string;
  status: RowStatus;
  message?: string;
  warnings: string[];
  error?: string | null;
}

const props = withDefaults(
  defineProps<{
    product: FactoryProduct;
    order: OrderDetail;
    mode?: 'all' | 'nested';
    nestSettings?: FactoryNestingSettings | null;
  }>(),
  { mode: 'all', nestSettings: null }
);

const emit = defineEmits<{ (e: 'close'): void }>();

const cache = createProductionAssetCache();
const engine = useProductionFileEngine(cache);

const phase = ref<'setup' | 'running' | 'preview' | 'done'>('running');
const items = ref<BatchItem[]>([]);
const cancelRequested = ref(false);
const completedCount = ref(0);
const zipping = ref(false);

// nested: setup state
const selectedSizes = ref<string[]>([]);
/** Nested: pack the whole order together, or one sheet set per roster row. */
const sheetGrouping = ref<'order' | 'row'>('order');
const labelOrderNo = ref(false);
const labelPlayer = ref(false);
const labelPanel = ref(false);

// both modes: panels to include. All preselected — the factory worker unticks
// panels sourced elsewhere or cut from dyed fabric, which the 3D model still
// needs but production must not print or nest.
const panelOptions = ref<{ name: string; label: string }[]>([]);
const selectedPanels = ref<string[]>([]);
const panelsLoading = ref(false);
const omittedPanels = computed(() =>
  panelOptions.value.map((o) => o.name).filter((name) => !selectedPanels.value.includes(name))
);
/** Unknown panels (none detected) are always kept — never silently drop artwork. */
function includePanel(name: string): boolean {
  return !panelOptions.value.length || selectedPanels.value.includes(name);
}
function selectAllPanels(): void {
  selectedPanels.value = panelOptions.value.map((o) => o.name);
}
const canGenerate = computed(
  () =>
    !panelsLoading.value &&
    (!panelOptions.value.length || selectedPanels.value.length > 0) &&
    (props.mode !== 'nested' || selectedSizes.value.length > 0)
);

/** Compose the first roster row once to learn the product's panel names. */
async function discoverPanels(): Promise<void> {
  panelsLoading.value = true;
  try {
    const first = allRows.value[0];
    if (!first) return;
    const result = await engine.compose(buildComposeInputForRow(props.product, first));
    panelOptions.value = result.panels.map((p) => ({
      name: p.name,
      label: prettyPanelName(p.name),
    }));
    selectAllPanels();
  } catch (error) {
    console.error('Panel detection failed', error);
    panelOptions.value = [];
    selectedPanels.value = [];
  } finally {
    panelsLoading.value = false;
  }
}

function startGenerate(): void {
  if (!canGenerate.value) return;
  if (props.mode === 'nested') startNested();
  else void runAllMode();
}

// nested: preview-adjustable settings (init from factory settings)
const paddingIn = ref(DEFAULT_PIECE_PADDING_IN);
const rotate90 = ref(true);
const rotate180 = ref(false);
const cutWidthMm = ref(1);
const searchOutline = ref(false);
const searchOffsetMm = ref(5);
const searchWidthMm = ref(1);

// nested: accumulators surviving into preview
const pieceContents = new Map<string, string>();
const allPieces: NestPieceInput[] = [];
let rootAttrs = '';
const packResult = ref<NestResult | null>(null);

const productLabel = computed(
  () => props.product.new_product_name ?? props.product.product_name ?? 'product'
);

const allRows = computed<ProductRosterDetail[]>(() => props.product.product_roster_detail ?? []);

const sizeOptions = computed(() => {
  const map = new Map<string, { size: string; rows: number; quantity: number }>();
  for (const row of allRows.value) {
    const size = (row.size ?? '').trim();
    const entry = map.get(size) ?? { size, rows: 0, quantity: 0 };
    entry.rows++;
    entry.quantity += rowQuantity(row);
    map.set(size, entry);
  }
  return [...map.values()];
});

const progressPercent = computed(() =>
  items.value.length ? Math.round((completedCount.value / items.value.length) * 100) : 0
);

const progressLabel = computed(() => {
  if (phase.value === 'running') {
    const current = items.value.find((i) => i.status === 'generating');
    return current
      ? `Generating ${completedCount.value + 1}/${items.value.length} — ${current.label}`
      : `Preparing… (${items.value.length} rows)`;
  }
  return cancelRequested.value
    ? `Cancelled after ${completedCount.value}/${items.value.length} rows`
    : `Finished ${completedCount.value}/${items.value.length} rows`;
});

const doneWithWarnings = computed(
  () =>
    phase.value === 'done' &&
    items.value.some((i) => i.status === 'warning' || i.status === 'error')
);

const rowsWithIssues = computed(
  () => items.value.filter((i) => i.status === 'warning' || i.status === 'error').length
);

const packedPieceCount = computed(() =>
  packResult.value ? packResult.value.sheets.reduce((n, s) => n + s.placements.length, 0) : 0
);

function statusDotClass(status: RowStatus): string {
  switch (status) {
    case 'done':
      return 'bg-green-500';
    case 'warning':
      return 'bg-amber-500';
    case 'error':
      return 'bg-red-500';
    case 'generating':
      return 'bg-blue-500 animate-pulse';
    case 'skipped':
      return 'bg-gray-300';
    default:
      return 'bg-gray-300';
  }
}

function rowLabel(row: ProductRosterDetail, index: number): string {
  const bits = [`Row ${index + 1}`, row.size || 'no size'];
  if (row.text) bits.push(String(row.text));
  if (row.number) bits.push(`#${row.number}`);
  return bits.join(' · ');
}

function rowQuantity(row: ProductRosterDetail): number {
  const qty = parseInt(String(row.quantity ?? 0), 10);
  return Number.isFinite(qty) && qty > 0 ? qty : 0;
}

function close(): void {
  if (phase.value === 'running') cancelRequested.value = true;
  else emit('close');
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

function effectiveSettings(): FactoryNestingSettings {
  return {
    ...(props.nestSettings ?? { paper_width: 63, paper_length: 118, allowed_rotations: [] }),
    allowed_rotations: [...(rotate90.value ? [90] : []), ...(rotate180.value ? [180] : [])],
    piece_padding: paddingIn.value,
    cut_outline_width_mm: cutWidthMm.value,
    search_outline: searchOutline.value,
    search_outline_offset_mm: searchOffsetMm.value,
    search_outline_width_mm: searchWidthMm.value,
  };
}

function validationTxt(): string {
  return buildValidationReport({
    orderNo: props.order.order_no,
    productName: productLabel.value,
    mode: props.mode,
    notes: omittedPanels.value.length
      ? [
          `Omitted panels (unticked in setup, left out of every file): ${omittedPanels.value
            .map((name) => `${prettyPanelName(name)} (${name})`)
            .join(', ')}`,
        ]
      : [],
    rows: items.value.map((i) => ({
      label: i.label,
      status: i.status,
      warnings: i.warnings,
      error: i.error,
    })),
  });
}

/** Print per-panel size labels onto a composite (best effort). */
async function labelComposite(
  svg: string,
  result: Awaited<ReturnType<ReturnType<typeof useProductionFileEngine>['compose']>>,
  size: string,
  quantity: number,
  warnings: string[] = []
): Promise<string> {
  try {
    if (!result.panels.length || !size.trim()) return svg;
    const marginUnits =
      (SIZE_LABEL_MARGIN_PT * result.viewBoxSize) / (result.heightPt || result.viewBoxSize);
    const all = await computePanelLabelPlacements(
      svg,
      result.panels,
      result.overlays,
      result.viewBoxSize,
      marginUnits,
      { silhouetteViewBox: true }
    );
    // Omitted panels simply never become a PowerClip; the composite is rebuilt
    // from the kept placements only. The fallback (a panel that could not be
    // isolated) keeps the whole composite, so omission can't apply there.
    const placements = all.filter((p) => includePanel(p.piece.panel.name));
    if (placements.length < all.length && placements.some((p) => p.piece.windowedFallback)) {
      warnings.push('panel-omit-not-applied');
    }
    // Anchor layer out, one PowerClip per panel (overlays and size indicator
    // inside), laser outlines under the artwork — same as the nested sheets.
    const finished = await finalizeCompositeForCorel(
      svg,
      placements,
      resolveOutlineSpec(props.nestSettings),
      { viewBoxSize: result.viewBoxSize, widthPt: result.widthPt, heightPt: result.heightPt },
      {
        sizeText: size,
        fontPt: SIZE_LABEL_FONT_PT,
        outlinePt: SIZE_LABEL_OUTLINE_PT,
        // Outlined with the bundled label font: curves Corel clips into the
        // PowerClip, not loose text — and independent of the design's fonts.
        font: await loadLabelFont(cache.font),
      }
    );
    // "<SIZE>-<qty>" header above the artwork, as on the factory's own sheets.
    return addHeaderLabelToComposite(finished, `${size.toUpperCase()}-${quantity || 1}`);
  } catch {
    return svg;
  }
}

function pieceLabelExtra(row: ProductRosterDetail, panelName: string): string | null {
  const parts: string[] = [];
  if (labelOrderNo.value && props.order.order_no != null) parts.push(`#${props.order.order_no}`);
  if (labelPlayer.value) {
    const player = [row.text ? String(row.text) : '', row.number ? `#${row.number}` : '']
      .filter(Boolean)
      .join(' ');
    if (player) parts.push(player);
  }
  if (labelPanel.value) parts.push(panelName);
  return parts.length ? parts.join(' · ') : null;
}

onMounted(() => {
  if (!allRows.value.length) {
    phase.value = 'done';
    showToast('This product has no roster rows to generate files for', 'warning');
    return;
  }
  if (!props.product.production_design_url) {
    phase.value = 'done';
    showToast('This product has no production design file', 'error');
    return;
  }

  if (props.mode === 'nested') {
    if (!props.nestSettings) {
      phase.value = 'done';
      showToast('No nesting settings available for this factory', 'error');
      return;
    }
    // Seed setup + preview state from the factory settings.
    const s = props.nestSettings;
    selectedSizes.value = sizeOptions.value.map((o) => o.size);
    labelOrderNo.value = !!s.label_order_no;
    labelPlayer.value = !!s.label_player;
    labelPanel.value = !!s.label_panel;
    paddingIn.value = s.piece_padding ?? DEFAULT_PIECE_PADDING_IN;
    rotate90.value = s.allowed_rotations?.includes(90) ?? false;
    rotate180.value = s.allowed_rotations?.includes(180) ?? false;
    cutWidthMm.value = s.cut_outline_width_mm ?? 1;
    searchOutline.value = !!s.search_outline;
    searchOffsetMm.value = s.search_outline_offset_mm ?? 5;
    searchWidthMm.value = s.search_outline_width_mm ?? 1;
    phase.value = 'setup';
    void discoverPanels();
    return;
  }

  // "Download All" also stops at setup now: the panel checklist applies to
  // both flows (a panel sourced elsewhere must leave every file).
  phase.value = 'setup';
  void discoverPanels();
});

function startNested(): void {
  const wanted = new Set(selectedSizes.value);
  const rows = allRows.value
    .map((row, rowIndex) => ({ row, rowIndex }))
    .filter(({ row }) => wanted.has((row.size ?? '').trim()));
  if (!rows.length) return;
  void runNested(rows);
}

// Which vector each logo gets (Logo inspector choice) — resolved once per
// product, shared by every row of the run. Notes are per product too.
const logoNotes: string[] = [];
let logoResolverPromise: ReturnType<typeof buildProductionLogoResolver> | null = null;
function logoResolver(): ReturnType<typeof buildProductionLogoResolver> {
  if (!logoResolverPromise) {
    logoResolverPromise = buildProductionLogoResolver(props.product.custom_logos, logoNotes);
  }
  return logoResolverPromise;
}

async function composeRow(item: BatchItem): Promise<{
  result: Awaited<ReturnType<ReturnType<typeof useProductionFileEngine>['compose']>>;
  svg: string;
} | null> {
  const result = await engine.compose(buildComposeInputForRow(props.product, item.row));

  if (!result.sizeFound) {
    item.status = 'error';
    item.error = `Size "${item.row.size}" not found in sizes SVG`;
    item.message = item.error;
    return null;
  }

  const rawInlined = await inlineCustomLogoVectors(result.svg, result.customLogoKeys, {
    storageUrl: productionStorageUrl(),
    vectorizeUrl: buildVectorizeLogoUrl,
    cache: cache.logoSvg,
    resolveUrl: await logoResolver(),
  });
  logoNotes.forEach((note) => {
    if (!item.warnings.includes(note)) item.warnings.push(note);
  });

  // Source atlases mask panel art with silhouette clips, which CorelDRAW's
  // importer drops wholesale — bake them into plain intersection geometry.
  // It also mangles rects under the composite's flip transforms, so basic
  // shapes become equivalent <path>s.
  const flattened = await flattenSourceClipPaths(rawInlined.svg);
  const svg = convertBasicShapesToPaths(flattened.svg).svg;

  item.warnings.push(...result.warnings, ...rawInlined.warnings);
  if (flattened.kept > 0) item.warnings.push(`corel-clips-remaining:${flattened.kept}`);
  return { result, svg };
}

function finishRow(item: BatchItem): void {
  if (item.status === 'generating') {
    if (item.warnings.length) {
      item.status = 'warning';
      item.message = item.warnings.includes('ungraded-base-size')
        ? 'ungraded (no sizes file)'
        : item.warnings[0];
    } else {
      item.status = 'done';
    }
  }
  completedCount.value++;
}

async function runAllMode(): Promise<void> {
  items.value = allRows.value.map((row, rowIndex) => ({
    rowIndex,
    row,
    label: rowLabel(row, rowIndex),
    status: 'pending' as RowStatus,
    warnings: [],
  }));
  phase.value = 'running';

  const zip = new JSZip();
  let generatedCount = 0;
  const overviewEntries: OverviewEntry[] = [];

  for (const item of items.value) {
    if (cancelRequested.value) {
      item.status = 'skipped';
      continue;
    }
    item.status = 'generating';
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      const composed = await composeRow(item);
      if (!composed) {
        completedCount.value++;
        continue;
      }
      const ungraded = item.warnings.includes('ungraded-base-size');
      const fileName = buildProductionFileName({
        orderNo: props.order.order_no,
        productName: productLabel.value,
        size: item.row.size,
        rowIndex: item.rowIndex,
        playerName: item.row.text,
        playerNumber: item.row.number,
        ungraded,
      });
      const labeled = await labelComposite(
        composed.svg,
        composed.result,
        item.row.size ?? '',
        Number(item.row.quantity) || 1,
        item.warnings
      );
      zip.file(fileName, labeled);
      overviewEntries.push({ size: item.row.size ?? '', svg: labeled });
      generatedCount++;
    } catch (error) {
      console.error('Production batch row failed', error);
      item.status = 'error';
      item.error = error instanceof Error ? error.message : 'generation failed';
      item.message = item.error;
    }
    finishRow(item);
  }

  phase.value = 'done';
  if (cancelRequested.value) {
    showToast('Download cancelled — no zip was created', 'warning');
    return;
  }
  if (!generatedCount) {
    showToast('No production files could be generated', 'error');
    return;
  }

  // Overview: every size laid out next to each other (one line per size),
  // not nested — the factory's at-a-glance sheet.
  const overview = buildOverviewSheet(overviewEntries);
  if (overview) zip.file('overview_sheet.svg', overview);

  zip.file('validation.txt', validationTxt());
  const orderPart = sanitizeFileSegment(props.order.order_no) || 'order';
  const productPart = sanitizeFileSegment(productLabel.value) || 'product';
  const blob = await zip.generateAsync({ type: 'blob' });
  downloadBlob(blob, `${orderPart}_${productPart}_production_files.zip`);
  showToast(`Generated ${generatedCount} production file(s)`, 'success');
}

async function runNested(
  rows: Array<{ row: ProductRosterDetail; rowIndex: number }>
): Promise<void> {
  items.value = rows.map(({ row, rowIndex }) => ({
    rowIndex,
    row,
    label: rowLabel(row, rowIndex),
    status: 'pending' as RowStatus,
    warnings: [],
  }));
  phase.value = 'running';
  let generatedCount = 0;

  for (const item of items.value) {
    if (cancelRequested.value) {
      item.status = 'skipped';
      continue;
    }
    item.status = 'generating';
    await new Promise((resolve) => setTimeout(resolve, 0));
    try {
      const qty = rowQuantity(item.row);
      if (!qty) {
        item.status = 'warning';
        item.message = 'quantity 0 — no pieces';
        item.warnings.push('quantity-zero');
        completedCount.value++;
        continue;
      }
      const composed = await composeRow(item);
      if (!composed) {
        completedCount.value++;
        continue;
      }
      const { result, svg } = composed;

      const content = extractCompositeContent(svg);
      if (!content || !result.panels.length) {
        item.status = 'error';
        item.error = content ? 'no panels detected' : 'could not parse composite';
        item.message = item.error;
        completedCount.value++;
        continue;
      }
      rootAttrs = rootAttrs || content.rootAttrs;

      // One isolated artwork per panel (only that panel's subtree + the
      // name/number/logo overlays that sit on it — never neighbor panels),
      // with a size-label position at the panel's exact geometric bottom
      // and the cut silhouette for the laser outlines.
      const labelMarginUnits =
        (SIZE_LABEL_MARGIN_PT * result.viewBoxSize) / (result.heightPt || result.viewBoxSize);
      const placementsForRow: PanelLabelPlacement[] = await computePanelLabelPlacements(
        svg,
        result.panels,
        result.overlays,
        result.viewBoxSize,
        labelMarginUnits,
        {
          silhouettePt: {
            compositeWidthPt: result.widthPt,
            compositeHeightPt: result.heightPt,
          },
        }
      );

      for (const [panelIndex, { piece: art, anchor, outlinePtD }] of placementsForRow.entries()) {
        // Panels the worker unticked in setup are not nested at all.
        if (!includePanel(art.panel.name)) continue;
        const contentKey = `r${item.rowIndex}p${panelIndex}`;
        pieceContents.set(contentKey, namespaceSvgIds(art.inner, `${contentKey}x`));
        const sizePt = windowSizePt(
          art.window,
          result.viewBoxSize,
          result.widthPt,
          result.heightPt
        );
        // A panel id that covers several disconnected pieces of fabric (the
        // inner panels of a reversible) becomes one piece per island — own
        // window, silhouette and label — sharing the panel artwork.
        const islands = outlinePtD
          ? await splitPieceIslands(outlinePtD, art.window, sizePt.widthPt, sizePt.heightPt)
          : null;
        if (islands) item.warnings.push(`panel-split-islands:${art.panel.name}:${islands.length}`);
        const parts = islands
          ? islands.map((island) => ({
              name: `${art.panel.name} ${island.index}`,
              idSuffix: `-i${island.index}`,
              window: island.window,
              widthPt: island.widthPt,
              heightPt: island.heightPt,
              outlineD: island.outlineD,
              anchor: undefined as typeof anchor | undefined,
            }))
          : [
              {
                name: art.panel.name,
                idSuffix: '',
                window: art.window,
                widthPt: sizePt.widthPt,
                heightPt: sizePt.heightPt,
                outlineD: outlinePtD ?? null,
                anchor,
              },
            ];
        for (const part of parts)
          for (let copy = 1; copy <= qty; copy++) {
            allPieces.push({
              id: `r${item.rowIndex + 1}-${sanitizeFileSegment(item.row.size) || 'nosize'}-${art.panel.name}${part.idSuffix}-c${copy}`,
              rowIndex: item.rowIndex,
              copyIndex: copy,
              size: item.row.size ?? '',
              panelName: part.name,
              widthPt: part.widthPt,
              heightPt: part.heightPt,
              window: part.window,
              contentKey,
              labelAnchor: part.anchor,
              // Isolated pieces need no sheet clip (ink-tight window); a
              // fallback piece's content is the whole composite, so clip it.
              // Island pieces share the panel artwork and rely on the sheet's
              // silhouette clip to show only their own island.
              needsClip: art.windowedFallback || !!islands,
              outlineD: part.outlineD,
              labelExtra: pieceLabelExtra(item.row, part.name),
            });
          }
      }

      if (result.warnings.includes('panel-namespace-missing')) {
        item.warnings.push('panel-namespace-missing');
      }
      if (placementsForRow.some((p) => p.piece.windowedFallback)) {
        item.warnings.push('panel-isolation-fallback');
      }
      if (placementsForRow.some((p) => !p.piece.windowedFallback && !p.outlinePtD)) {
        item.warnings.push('laser-outline-missing');
      }
      generatedCount++;
    } catch (error) {
      console.error('Production batch row failed', error);
      item.status = 'error';
      item.error = error instanceof Error ? error.message : 'generation failed';
      item.message = item.error;
    }
    finishRow(item);
  }

  if (cancelRequested.value) {
    phase.value = 'done';
    showToast('Cancelled — nothing was packed', 'warning');
    return;
  }
  if (!generatedCount || !allPieces.length) {
    phase.value = 'done';
    showToast('No panels could be generated', 'error');
    return;
  }

  // Fabric area per piece (from the cut silhouette) so the utilization
  // figure counts cloth, not bounding boxes.
  const areas = await computePieceAreas(allPieces);
  for (const piece of allPieces) piece.areaPt2 = areas.get(piece.id) ?? null;

  await repack();
  phase.value = 'preview';
}

/** True while the shape nester runs (it takes seconds on a big order). */
const packing = ref(false);

async function repack(): Promise<void> {
  const options = {
    perRow: sheetGrouping.value === 'row',
    rowLabels: new Map(items.value.map((item) => [item.rowIndex, item.label])),
  };
  packing.value = true;
  try {
    // Shape-aware nesting: real silhouettes on a 2.5 mm grid, so pieces can sit
    // in each other's concavities. Falls back to the rectangle packer if the
    // geometry engine is unavailable.
    packResult.value = await nestPieces(allPieces, effectiveSettings(), undefined, options);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[nesting] shape nesting failed, using rectangle packing', error);
    packResult.value = packPieces(allPieces, effectiveSettings(), undefined, options);
  } finally {
    packing.value = false;
  }
}

async function downloadNestedZip(): Promise<void> {
  const result = packResult.value;
  if (!result?.sheets.length) return;
  zipping.value = true;
  try {
    const settings = effectiveSettings();
    const outlineSpec = resolveOutlineSpec(settings);
    // True offset curves for the searching outline (stroke only, no fill).
    const searchOutlines = await computeSearchOutlines(allPieces, outlineSpec);
    // Size indicators as glyph outlines so they clip into each piece's PowerClip.
    const labelFont = await loadLabelFont(cache.font);
    // Labels laid along each piece's inset silhouette (diamond edges, collar
    // bands) — falls back to the straight centred label per piece.
    const edgeLabels = await computeEdgeLabels(allPieces, labelFont);
    const ctx = (sheetless = false, clipPaths?: Array<string | null>) => ({
      paperWidthPt: result.paperWidthPt,
      paperLengthPt: result.paperLengthPt,
      contents: sheetless ? new Map<string, string>() : pieceContents,
      rootAttrs,
      outlineSpec,
      searchOutlines,
      clipPaths,
      labelFont,
      edgeLabels,
    });

    const zip = new JSZip();
    let dxfFailed = false;
    // The factory also wants the whole run in ONE file: every sheet laid out
    // in a grid (one grid row per roster entry in per-row mode).
    const combinedSheets: CombinedSheetEntry[] = [];
    const combinedCutlines: CombinedSheetEntry[] = [];
    for (let i = 0; i < result.sheets.length; i++) {
      const sheet = result.sheets[i];
      // One PowerClip per placed piece: clip silhouettes baked into sheet space.
      const clipPaths = await computeSheetClipPaths(sheet, outlineSpec);
      const stem = sheetFileStem(i, sheet);
      const sheetSvg = buildSheetSvg(sheet, ctx(false, clipPaths));
      const cutlinesSvg = buildCutlinesSvg(sheet, ctx(true));
      const caption = [
        stem,
        sheet.group ?? '',
        sheet.group && sheet.garments?.length ? `garments ${garmentRange(sheet.garments)}` : '',
      ]
        .filter(Boolean)
        .join(' — ');
      combinedSheets.push({ svg: sheetSvg, group: sheet.group, caption });
      combinedCutlines.push({ svg: cutlinesSvg, group: sheet.group, caption });
      const dxf = await buildCutlinesDxf(sheet, ctx(true));
      if (dxf) zip.file(`${stem}_cutlines.dxf`, dxf);
      else dxfFailed = true;
    }
    const combinedOpts = {
      paperWidthPt: result.paperWidthPt,
      paperLengthPt: result.paperLengthPt,
      perRow: sheetGrouping.value === 'row',
      rootAttrs,
    };
    const allSheets = buildCombinedSheetsSvg(combinedSheets, combinedOpts);
    if (allSheets) zip.file('all_sheets.svg', allSheets);
    const allCutlines = buildCombinedSheetsSvg(combinedCutlines, combinedOpts);
    if (allCutlines) zip.file('all_sheets_cutlines.svg', allCutlines);
    zip.file(
      'nesting_summary.txt',
      buildNestingSummary({
        orderNo: props.order.order_no,
        productName: productLabel.value,
        settings,
        paddingIn: paddingIn.value,
        result,
        totalPieces: allPieces.length,
        omittedPanels: omittedPanels.value.map((name) => `${prettyPanelName(name)} (${name})`),
        perRow: sheetGrouping.value === 'row',
      })
    );
    zip.file('validation.txt', validationTxt());

    if (result.oversized.length) {
      showToast(
        `${result.oversized.length} piece(s) exceed the paper and were left out — see nesting_summary.txt`,
        'warning'
      );
    }
    if (dxfFailed) showToast('DXF cut files could not be generated for this run', 'warning');

    const orderPart = sanitizeFileSegment(props.order.order_no) || 'order';
    const productPart = sanitizeFileSegment(productLabel.value) || 'product';
    const blob = await zip.generateAsync({ type: 'blob' });
    downloadBlob(blob, `${orderPart}_${productPart}_nested_files.zip`);
    showToast(
      `Nested ${packedPieceCount.value} piece(s) onto ${result.sheets.length} sheet(s)`,
      'success'
    );
  } finally {
    zipping.value = false;
  }
}
</script>
